<template>
  <span class="relative forced-color-none">
    <base-picture
      v-if="thumbnail"
      v-style="{ inset }"
      class="absolute-0 overflow-hidden"
      :class="classInner"
      height="32"
      :lazy
      :src="thumbnail"
      width="32"
    />
    <span
      v-else
      v-style="{ inset }"
      class="absolute-0 b b-grey-80"
      :class="[classInner, { 'bg-grey-60': isWhite }]"
      :style="{ background: bg }"
    />
    <slot />
  </span>
</template>

<script lang="ts" setup>
import type { CSSClass, Responsive } from '#types/common'

const { id, color = [], thumbnail } = defineProps<{
  /**
   * The ID of the color filter to check for custom values
   */
  id?: string
  /**
   * Color value as an array of color codes or 'multicolor'
   */
  color?: string[]
  /**
   * Optional thumbnail that will take precedence over color
   */
  thumbnail?: string
  classInner?: CSSClass
  inset?: Responsive | string
  lazy?: boolean
}>()

const bg = (() => {
  if (!color?.length) return
  if (color[0] === 'multicolor') {
    // if props.color includes 'multicolor', bg is a rainbow conic gradient
    return 'conic-gradient(from 90deg, violet, indigo, blue, green, yellow, orange, red, violet)'
  }
  if (id?.toLowerCase()?.includes('checkerboard')) {
    const [color1, color2 = '#FFFFFF'] = color
    if (color1)
      return `repeating-conic-gradient(from 0deg, ${color2} 0% 25%, ${color1} 0% 50%) 50% / 0.375rem 0.375rem`
  }
  if (color.length === 1) {
    return color[0]
  }
  // if props.color includes more than one color, bg is a linear gradient with color stops for each color value
  else {
    return `linear-gradient(45deg,
    ${color.map((c, i) => `${c} ${(i * 100) / color.length}% ${((i + 1) * 100) / color.length}%`).join(', ')
  })`
  }
})()

const isWhite = bg?.toLowerCase() === '#ffffff'
</script>
